/**
 * useBackgroundGenPoller — 백그라운드 생성 완료 감지 폴링 훅
 *
 * backgroundGenStore.current가 있을 때만 활성화.
 * 5초 간격으로 최근 generation을 폴링하여 서버 완료를 감지한다.
 * 최대 5분(60회) 후 자동 타임아웃.
 */

import { useEffect, useRef } from 'react'
import { apiFetch } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'
import { useBackgroundGenStore } from '@/stores/backgroundGenStore'
import { useUIStore } from '@/stores/uiStore'

const POLL_INTERVAL = 5_000
const MAX_POLLS = 60

interface GenerationsResponse {
  generations: Array<{
    id: string
    generatedUrls: string[] | null
    createdAt: string
  }>
}

export function useBackgroundGenPoller() {
  const current = useBackgroundGenStore((s) => s.current)
  const clear = useBackgroundGenStore((s) => s.clear)
  const pollCountRef = useRef(0)

  useEffect(() => {
    if (!current) {
      pollCountRef.current = 0
      return
    }

    pollCountRef.current = 0

    const interval = setInterval(async () => {
      pollCountRef.current++

      if (pollCountRef.current > MAX_POLLS) {
        clearInterval(interval)
        clear()
        useUIStore.getState().addToast({
          type: 'error',
          message: '생성 상태를 확인할 수 없습니다. 새로고침해주세요.',
          duration: 5000,
        })
        return
      }

      try {
        const res = await apiFetch<GenerationsResponse>('/api/ai/generations?pageSize=1')
        if (res.success) {
          const latest = res.data.generations[0]
          if (latest && latest.createdAt > current.startedAt) {
            clearInterval(interval)
            clear()
            queryClient.invalidateQueries({ queryKey: ['/api/ai/generations?pageSize=8'] })
            queryClient.invalidateQueries({ queryKey: ['/api/ai/albums'] })

            const successCount = latest.generatedUrls?.length ?? 0
            const failureCount = current.count - successCount
            const message = failureCount > 0
              ? `사진 ${successCount}장 생성 완료 (${failureCount}장 실패)`
              : `사진 ${successCount}장 생성이 완료되었습니다!`

            useUIStore.getState().addToast({
              type: failureCount > 0 && successCount > 0 ? 'info' : 'success',
              message,
              duration: 5000,
            })
          }
        }
      } catch {
        // 네트워크 에러 — 다음 폴링에서 재시도
      }
    }, POLL_INTERVAL)

    return () => clearInterval(interval)
  }, [current, clear])

  return current
}
